import React, { forwardRef } from "react";
import { flexRender, Row } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, ChevronRight } from "lucide-react";
import NoData from "@/assets/noData.svg";
import { cn } from "@/lib/utils";
import RecursiveSubRows from "./RecursiveSubRows";
import { DataTableCollapsibleProps, DataTableCollapsibleRef } from "./types";
import Footer from "./Footer";
import useDataTableCollapsibleRow from "./useDataTableCollapsibleRow";
import SortableTableHead from "../DataTable/SortableTableHead";

const DataTableCollapsibleRow = forwardRef<
  DataTableCollapsibleRef,
  DataTableCollapsibleProps
>(
  (
    {
      data = [],
      columns,
      variant,
      nestedColumns = [], // 👈 Array of column definitions per level (e.g., [level2Cols, level3Cols])
      onExpand,
      onExpandStateChange,
      renderExpandedContent,
      separateExpandColumn = false,
      globalFilter,
      setGlobalFilter,
      rowSelection,
      setRowSelection,
      setPageNumber,
      setPageSize,
      serverSidePagination = false,
      totalPages,
      pageNumber = 0,
      pageSize = 10,
      totalRecords,
      hideFooter = false,
      paginationArr = [5, 10, 20, 50],
      containerClassName,
      tableWrapperClassName,
      expandedCellClassName,
      allowMultipleExpansion = true,
      lastRow,
      icons,
      hideHeader,
      resetPageIndex = false,
      sorting,
      onSortingChange,
      enableSorting = true,
      manualSorting,
    },
    ref
  ) => {
    const {
      isSecondary,
      computedTotalRecords,
      toggleChildExpanded,
      isChildExpanded,
      handleRowExpand,
      table,
      pagination,
      setPagination,
    } = useDataTableCollapsibleRow({
      data,
      columns,
      variant,
      onExpand,
      onExpandStateChange,
      globalFilter,
      setGlobalFilter,
      rowSelection,
      setRowSelection,
      setPageNumber,
      setPageSize,
      serverSidePagination,
      totalPages,
      pageNumber,
      pageSize,
      totalRecords,
      hideFooter,
      allowMultipleExpansion,
      resetPageIndex,
      sorting,
      onSortingChange,
      enableSorting,
      manualSorting,
      ref,
    });

    const renderExpandButton = (row: Row<any>) => (
      <button
        type="button"
        onClick={() => handleRowExpand(row)}
        className="flex items-center gap-2 border border-primary rounded-full p-0.5 hover:bg-primary/10 transition-colors"
        aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
      >
        {row.getIsExpanded() ? (
          <ChevronDown className="w-4 h-4 text-primary" />
        ) : (
          <ChevronRight className="w-4 h-4 text-primary" />
        )}
      </button>
    );

    return (
      <div
        className={cn(
          "rounded-lg border border-gray-200 shadow-md overflow-hidden bg-white w-full",
          containerClassName
        )}
      >
        {/* Table Wrapper */}
        <div
          className={cn(
            "relative lg:w-full",
            isSecondary ? "p-0 shadow-md" : "px-4",
            tableWrapperClassName
          )}
        >
          <Table className="relative w-full border-collapse">
            <TableHeader
              className={cn(
                isSecondary ? "bg-gray-100" : "bg-white"
              )}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="bg-inherit">
                  {separateExpandColumn && (
                    <TableHead className="text-left h-[50px] text-primary w-[44px] bg-inherit" />
                  )}
                  {headerGroup.headers.map((header) => (
                    <SortableTableHead
                      key={header.id}
                      header={header}
                      className={cn(
                        (header.column.columnDef.meta as any)?.align === "right"
                          ? "text-right"
                          : "text-left",
                        "h-[50px] font-semibold text-primary bg-inherit"
                      )}
                    />
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, index) => {
                  (row as any).tableMeta = {
                    toggleChildExpanded,
                    isChildExpanded,
                  };

                  return (
                    <React.Fragment key={row.id}>
                      {/* Parent Row */}
                      <TableRow
                        className={cn(
                          "border-gray-200 transition-colors",
                          isSecondary
                            ? row.getIsExpanded()
                              ? "border-0"
                              : "border"
                            : "",
                          index % 2 === 1 ? "bg-gray-50/50" : "bg-white"
                        )}
                      >
                        {separateExpandColumn && (
                          <TableCell className="h-[45px] w-[44px]">
                            {renderExpandButton(row)}
                          </TableCell>
                        )}

                        {row.getVisibleCells().map((cell, i) => {
                          const size = cell.column.columnDef.size;

                          return (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                (cell.column.columnDef.meta as any)?.align === "right"
                                  ? "text-right"
                                  : "text-left",
                                "h-[45px]"
                              )}
                            >
                              {i === 0 && !separateExpandColumn ? (
                                <div
                                  className="flex items-center gap-2"
                                  style={{ width: size ? size : "auto" }}
                                >
                                  {renderExpandButton(row)}
                                  {icons?.level1 || null}
                                  <span className="text-wrap">
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </span>
                                </div>
                              ) : (
                                flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {/* Dynamic Nested Sub-Rows */}
                      {row.getIsExpanded() && (
                        <RecursiveSubRows
                          row={row}
                          nestedColumns={nestedColumns} // 👈 Dynamic array passed down
                          levelIndex={0} // 👈 Start at index 0 for Level 2
                          expandedCellClassName={expandedCellClassName}
                          renderExpandedContent={renderExpandedContent}
                          separateExpandColumn={separateExpandColumn}
                          icons={icons}
                          hideHeader={hideHeader}
                          isSecondary={isSecondary}
                        />
                      )}

                      {lastRow || null}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow className="w-full">
                  <TableCell
                    colSpan={columns.length + (separateExpandColumn ? 1 : 0)}
                    className="py-10 text-center"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <img
                        src={NoData}
                        className="w-[200px] h-auto"
                        alt="No data available"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <Footer
          hideFooter={hideFooter}
          computedTotalRecords={computedTotalRecords}
          pagination={pagination}
          setPagination={setPagination}
          paginationArr={paginationArr}
          table={table}
        />
      </div>
    );
  }
);

DataTableCollapsibleRow.displayName = "DataTableCollapsibleRow";

export default DataTableCollapsibleRow;
